/**
 * Data Deletion Service for COPPA Compliance
 * Provides comprehensive data deletion functionality for parents and users
 */

import { 
  collection, 
  query, 
  where, 
  getDocs, 
  deleteDoc, 
  doc, 
  writeBatch,
  orderBy,
  limit
} from 'firebase/firestore';
import { 
  ref, 
  deleteObject, 
  listAll, 
  getDownloadURL 
} from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../firebase';
import { User } from '../types';

export interface DeletionResult {
  success: boolean;
  deletedItems: {
    lessons: number;
    practiceSessions: number;
    feedEvents: number;
    storageFiles: number;
    userProfile: boolean;
  };
  errors: string[];
}

export interface ParentalDeletionOptions {
  deleteAllData: boolean;
  deleteLessons: boolean;
  deletePracticeData: boolean;
  deleteSocialData: boolean;
  keepAccount: boolean; // If false, will delete the entire account
}

class DataDeletionService {
  /**
   * Delete all data for a user (parental deletion)
   * @param userId - User ID to delete data for
   * @param options - Deletion options
   * @returns Deletion result
   */
  async deleteUserData(userId: string, options: ParentalDeletionOptions): Promise<DeletionResult> {
    const result: DeletionResult = {
      success: true,
      deletedItems: {
        lessons: 0,
        practiceSessions: 0,
        feedEvents: 0,
        storageFiles: 0,
        userProfile: false
      },
      errors: []
    };

    try {
      // Delete lessons
      if (options.deleteLessons || options.deleteAllData) {
        const lessonsDeleted = await this.deleteLessons(userId);
        result.deletedItems.lessons = lessonsDeleted;
      }

      // Delete practice sessions
      if (options.deletePracticeData || options.deleteAllData) {
        const sessionsDeleted = await this.deletePracticeSessions(userId);
        result.deletedItems.practiceSessions = sessionsDeleted;
      }

      // Delete social/feed data
      if (options.deleteSocialData || options.deleteAllData) {
        const feedEventsDeleted = await this.deleteFeedEvents(userId);
        result.deletedItems.feedEvents = feedEventsDeleted;
      }

      // Delete storage files (avatars, etc.)
      const storageFilesDeleted = await this.deleteStorageFiles(userId);
      result.deletedItems.storageFiles = storageFilesDeleted;

      // Delete user profile and account
      if (options.keepAccount) {
        // Only delete data, keep account
        await this.deleteUserProfile(userId);
        result.deletedItems.userProfile = true;
      } else {
        // Delete entire account
        await this.deleteUserAccount(userId);
        result.deletedItems.userProfile = true;
      }

    } catch (error) {
      console.error('Error during data deletion:', error);
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown error during deletion');
    }

    return result;
  }

  /**
   * Delete all lessons for a user
   */
  private async deleteLessons(userId: string): Promise<number> {
    try {
      const lessonsQuery = query(
        collection(db, 'lessons'),
        where('studentId', '==', userId)
      );
      
      const querySnapshot = await getDocs(lessonsQuery);
      const batch = writeBatch(db);
      
      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      await batch.commit();
      return querySnapshot.size;
    } catch (error) {
      console.error('Error deleting lessons:', error);
      throw error;
    }
  }

  /**
   * Delete all practice sessions for a user
   */
  private async deletePracticeSessions(userId: string): Promise<number> {
    try {
      const sessionsQuery = query(
        collection(db, 'practiceSessions'),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(sessionsQuery);
      const batch = writeBatch(db);
      
      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      await batch.commit();
      return querySnapshot.size;
    } catch (error) {
      console.error('Error deleting practice sessions:', error);
      throw error;
    }
  }

  /**
   * Delete all feed events for a user
   */
  private async deleteFeedEvents(userId: string): Promise<number> {
    try {
      const feedQuery = query(
        collection(db, 'feedEvents'),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(feedQuery);
      const batch = writeBatch(db);
      
      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      await batch.commit();
      return querySnapshot.size;
    } catch (error) {
      console.error('Error deleting feed events:', error);
      throw error;
    }
  }

  /**
   * Delete all storage files for a user
   */
  private async deleteStorageFiles(userId: string): Promise<number> {
    try {
      let deletedCount = 0;
      
      // Delete avatar files
      const avatarRef = ref(storage, `avatars/${userId}`);
      const avatarList = await listAll(avatarRef);
      
      for (const item of avatarList.items) {
        try {
          await deleteObject(item);
          deletedCount++;
        } catch (error) {
          console.warn('Could not delete avatar file:', item.fullPath, error);
        }
      }

      // Delete any remaining temp audio files (should be empty due to processing deletion)
      const tempAudioRef = ref(storage, `tempAudio/${userId}`);
      const tempAudioList = await listAll(tempAudioRef);
      
      for (const item of tempAudioList.items) {
        try {
          await deleteObject(item);
          deletedCount++;
        } catch (error) {
          console.warn('Could not delete temp audio file:', item.fullPath, error);
        }
      }

      return deletedCount;
    } catch (error) {
      console.error('Error deleting storage files:', error);
      throw error;
    }
  }

  /**
   * Delete user profile data (but keep account)
   */
  private async deleteUserProfile(userId: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await deleteDoc(userRef);
    } catch (error) {
      console.error('Error deleting user profile:', error);
      throw error;
    }
  }

  /**
   * Delete entire user account
   */
  private async deleteUserAccount(userId: string): Promise<void> {
    try {
      // Use the existing deleteUserCompletely function
      const deleteUserCompletely = httpsCallable(functions, 'deleteUserCompletely');
      await deleteUserCompletely({ userId });
    } catch (error) {
      console.error('Error deleting user account:', error);
      throw error;
    }
  }

  /**
   * Get data summary for a user (for parental review)
   */
  async getUserDataSummary(userId: string): Promise<{
    lessons: number;
    practiceSessions: number;
    feedEvents: number;
    storageFiles: number;
    accountAge: number;
  }> {
    try {
      const [lessonsQuery, sessionsQuery, feedQuery] = await Promise.all([
        getDocs(query(collection(db, 'lessons'), where('studentId', '==', userId))),
        getDocs(query(collection(db, 'practiceSessions'), where('userId', '==', userId))),
        getDocs(query(collection(db, 'feedEvents'), where('userId', '==', userId)))
      ]);

      // Get storage files count
      let storageFileCount = 0;
      try {
        const avatarRef = ref(storage, `avatars/${userId}`);
        const avatarList = await listAll(avatarRef);
        storageFileCount += avatarList.items.length;
      } catch (error) {
        console.warn('Could not count avatar files:', error);
      }

      // Get account age
      const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', userId)));
      let accountAge = 0;
      if (!userDoc.empty) {
        const userData = userDoc.docs[0].data();
        if (userData.createdAt) {
          const createdDate = userData.createdAt.toDate ? userData.createdAt.toDate() : new Date(userData.createdAt);
          accountAge = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24)); // days
        }
      }

      return {
        lessons: lessonsQuery.size,
        practiceSessions: sessionsQuery.size,
        feedEvents: feedQuery.size,
        storageFiles: storageFileCount,
        accountAge
      };
    } catch (error) {
      console.error('Error getting user data summary:', error);
      throw error;
    }
  }

  /**
   * Check if user is under 13 and requires parental consent
   */
  async isUserUnder13(userId: string): Promise<boolean> {
    try {
      const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', userId)));
      if (userDoc.empty) return false;
      
      const userData = userDoc.docs[0].data();
      if (!userData.birthdate) return false;
      
      const today = new Date();
      const birthDate = new Date(userData.birthdate);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      return age < 13 || (age === 13 && monthDiff < 0) || (age === 13 && monthDiff === 0 && today.getDate() < birthDate.getDate());
    } catch (error) {
      console.error('Error checking user age:', error);
      return false;
    }
  }
}

export const dataDeletionService = new DataDeletionService();
